import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Consent from "@/pages/Consent";
import NewAnalysis from "@/pages/NewAnalysis";
import Analysis from "@/pages/Analysis";
import MyPlants from "@/pages/MyPlants";
import PlantDetail from "@/pages/PlantDetail";
import Consultations from "@/pages/Consultations";
import ConsultationRequest from "@/pages/ConsultationRequest";
import Profile from "@/pages/Profile";
import Privacy from "@/pages/Privacy";
import ProCases from "@/pages/pro/ProCases";
import ProCase from "@/pages/pro/ProCase";
import ProUses from "@/pages/pro/ProUses";
import ProUsers from "@/pages/pro/ProUsers";
import ProPackages from "@/pages/pro/ProPackages";
import CompleteProfile from "@/pages/CompleteProfile";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Terms from "@/pages/Terms";
import BuyCredits from "@/pages/BuyCredits";
import PaymentResult from "@/pages/PaymentResult";
import VerifyEmail from "@/pages/VerifyEmail";
import { Loader } from "@/components/Common";

function Protected({ children, pro }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader text="Verifica accesso…" />;
  if (!user) return <Navigate to="/accedi" state={{ from: loc.pathname + loc.search }} replace />;
  if (pro && user.role !== "professional") return <Navigate to="/" replace />;
  return children;
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.getElementById("app-scroll")?.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (user?.needs_profile && !["/completa-profilo", "/privacy", "/termini"].includes(location.pathname))
    return <Navigate to="/completa-profilo" state={{ from: location.pathname + location.search }} replace />;
  const P = (el, pro) => <Protected pro={pro}>{el}</Protected>;
  return (
    <Layout>
      <ScrollTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/accedi" element={<Login />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/termini" element={<Terms />} />
        <Route path="/acquista" element={P(<BuyCredits />)} />
        <Route path="/pagamento/esito" element={P(<PaymentResult />)} />
        <Route path="/password-dimenticata" element={<ForgotPassword />} />
        <Route path="/reimposta-password" element={<ResetPassword />} />
        <Route path="/verifica-email" element={<VerifyEmail />} />
        <Route path="/consenso" element={P(<Consent />)} />
        <Route path="/analisi/nuova" element={P(<NewAnalysis />)} />
        <Route path="/analisi/:id" element={P(<Analysis />)} />
        <Route path="/piante" element={P(<MyPlants />)} />
        <Route path="/piante/:id" element={P(<PlantDetail />)} />
        <Route path="/consulenze" element={P(<Consultations />)} />
        <Route path="/consulenza/:analysisId" element={P(<ConsultationRequest />)} />
        <Route path="/profilo" element={P(<Profile />)} />
        <Route path="/pro" element={P(<ProCases />, true)} />
        <Route path="/pro/casi/:id" element={P(<ProCase />, true)} />
        <Route path="/pro/impieghi" element={P(<ProUses />, true)} />
        <Route path="/pro/utenti" element={P(<ProUsers />, true)} />
        <Route path="/pro/pacchetti" element={P(<ProPackages />, true)} />
        <Route path="/completa-profilo" element={P(<CompleteProfile />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
